/* Tour "invoices-overview" — découverte métier des échéances (factures) :
   créer une facture de test, l'ouvrir, comprendre les champs clés (statut,
   date prévisionnelle, date de paiement), voir comment elle se projette dans
   le dashboard, puis nettoyer. Termine en proposant de connecter des outils.

   La facture de test est créée/ouverte/fermée par InvoicesWrapper via le bus
   (il connaît le payload et les refs). La suppression finale passe par le
   store pour rester robuste même hors de la page Échéances. */

import store from '@/store';
import router from '@/router/router';
import AppBus from '@/bus/app-bus';
import './types';

function readInvoice(): {id: string; projection_date?: string} | null {
  try {
    return JSON.parse(localStorage.getItem('fygr-tour-invoice') || 'null');
  } catch {
    return null;
  }
}

async function deleteTestInvoice() {
  document.getElementById('tour-row-span')?.remove();
  const ref = readInvoice();
  if (!ref || !ref.id) return;
  try {
    await store.dispatch('invoices/actionDeleteInvoice', {
      selectedIds: [ref.id],
      income_or_expense: 'income',
      selectedAll: false,
      ignoreIds: [],
    });
  } catch {
    /* on réessaiera via le filet de InvoicesWrapper */
    return;
  }
  try {
    localStorage.removeItem('fygr-tour-invoice');
  } catch {
    /* ignore */
  }
}

/** Repère la ligne « Non catégorisées » des Entrées (valeurs non nulles), la
 *  fait défiler au centre et renvoie un sélecteur. Fallback : ligne Entrées. */
const numFR = (s: string | null) =>
  parseFloat((s || '').replace(/[^\d,.-]/g, '').replace(',', '.'));

// Scrolle tout en bas chaque conteneur scrollable autour du tableau (le
// tableau peut être virtualisé : les lignes du bas n'existent qu'une fois
// scrollées).
function scrollTableToBottom() {
  const table = document.querySelector('.cash-table');
  let node: Element | null = table;
  while (node && node !== document.body) {
    const el = node as HTMLElement;
    if (el.scrollHeight > el.clientHeight + 4) el.scrollTop = el.scrollHeight;
    node = node.parentElement;
  }
  window.scrollTo(0, document.body.scrollHeight);
}

// Renvoie la <tr> libellée « Non catégorisées » dont les valeurs sont non
// nulles (les Entrées) ; sinon la dernière trouvée.
function findUncatRow(): Element | null {
  const labels = Array.from(document.querySelectorAll('.cash-table th')).filter(
    (th) => (th.textContent || '').includes('Non catégor'),
  );
  const hasValue = (row: Element | null) => {
    const cls = row
      ? Array.from(row.classList).find((c) => /^tr\d+$/.test(c))
      : null;
    if (!cls) return false;
    const dataRow = Array.from(document.querySelectorAll('.' + cls)).find((r) =>
      r.querySelector('.cash-cell'),
    );
    return (
      !!dataRow &&
      Array.from(dataRow.querySelectorAll('.cash-cell')).some((td) => {
        const n = numFR(td.textContent);
        return Number.isFinite(n) && n !== 0;
      })
    );
  };
  for (const th of labels) {
    if (hasValue(th.closest('tr'))) return th.closest('tr');
  }
  return null;
}

// Construit un rectangle de surbrillance englobant le libellé (table gauche)
// et la cellule de valeur (table droite). Renvoie true si construit.
function buildUncatSpan(): boolean {
  const chosen = findUncatRow();
  if (!chosen) return false;
  try {
    chosen.scrollIntoView({block: 'center'});
  } catch {
    /* noop */
  }
  const cls = Array.from(chosen.classList).find((c) => /^tr\d+$/.test(c));
  const dataRow = cls
    ? Array.from(document.querySelectorAll('.' + cls)).find((r) =>
        r.querySelector('.cash-cell'),
      )
    : null;
  const valueCell = dataRow
    ? Array.from(dataRow.querySelectorAll('.cash-cell')).find((td) => {
        const n = numFR(td.textContent);
        return Number.isFinite(n) && n !== 0;
      })
    : null;
  const a = chosen.getBoundingClientRect();
  const b = (valueCell || dataRow || chosen).getBoundingClientRect();
  const span =
    document.getElementById('tour-row-span') || document.createElement('div');
  span.id = 'tour-row-span';
  span.style.cssText = [
    'position:fixed',
    `top:${Math.min(a.top, b.top)}px`,
    `left:${Math.min(a.left, b.left)}px`,
    `width:${Math.max(a.right, b.right) - Math.min(a.left, b.left)}px`,
    `height:${Math.max(a.bottom, b.bottom) - Math.min(a.top, b.top)}px`,
    'pointer-events:none',
    'z-index:1',
  ].join(';');
  if (!span.parentNode) document.body.appendChild(span);
  return true;
}

// Cible de l'étape dashboard : le repère s'il a été construit, sinon on tente
// de le construire à la volée, sinon fallback ligne Entrées.
function focusUncategorizedRow(): string {
  if (document.getElementById('tour-row-span')) return '#tour-row-span';
  if (buildUncatSpan()) return '#tour-row-span';
  return document.querySelector('.trmrow-1')
    ? '.trmrow-1'
    : '[data-tour-step="cash-income-row"]';
}

// On déclenche la création de la facture SEULEMENT au démarrage de l'étape 1
// (donc après « Commencer »), pas avant. La popup de création s'ouvre alors
// pendant que le voile du tuto est actif (z-index 8900) → elle est cachée
// dessous (les popups sont en z-index ~300).
let seedRequested = false;
// Instant où l'œil démo est apparu dans la liste : on laisse un court délai de
// stabilisation avant de caler la bulle, le temps que le tableau réordonne/
// insère la ligne (sinon on peut viser transitoirement une ligne voisine si
// l'utilisateur a déjà des factures).
let demoEyeSeenAt = 0;
const DEMO_ROW_SETTLE_MS = 600;

// Cible dynamique de la LIGNE de la facture de démo. Tant que la facture n'est
// pas dans la liste, on déclenche le seed (une fois) et on renvoie null : le
// moteur re-résout jusqu'à ce qu'elle apparaisse. On (re)marque la <tr> à chaque
// appel (le tableau recycle ses lignes).
function demoRowSelector(): string | null {
  const eye = document.querySelector('[data-tour-step="inv-eye-demo"]');
  if (!eye) {
    demoEyeSeenAt = 0;
    if (!seedRequested) {
      seedRequested = true;
      AppBus.$emit('tour:invoice-seed');
    }
    return null;
  }
  // Laisse le tableau se stabiliser avant de verrouiller la cible.
  if (!demoEyeSeenAt) demoEyeSeenAt = Date.now();
  if (Date.now() - demoEyeSeenAt < DEMO_ROW_SETTLE_MS) return null;
  document
    .querySelectorAll('[data-tour-row="invoice-demo"]')
    .forEach((n) => n.removeAttribute('data-tour-row'));
  const row = eye.closest('tr');
  if (!row) return null;
  row.setAttribute('data-tour-row', 'invoice-demo');
  return '[data-tour-row="invoice-demo"]';
}

window.FygrTour.register('invoices-overview', {
  title: 'Découvrir les échéances',
  page: '/invoices',
  interactive: false,
  intro: {
    title: 'Vos échéances',
    body: 'Les échéances, ce sont vos factures clients et fournisseurs. En les renseignant, Okimia projette ces flux dans votre trésorerie à la bonne date.',
    note: 'Une facture d’exemple est utilisée pour la démonstration : elle est supprimée automatiquement à la fin.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Modules ».',
  },
  // Rien pendant l'intro : la facture n'est créée qu'au début de l'étape 1
  // (cf. demoRowSelector), donc après « Commencer ».
  onStart: () => {
    seedRequested = false;
    demoEyeSeenAt = 0;
  },
  // Quitter (Passer/Quitter/Échap) ou fin : on supprime la facture de démo.
  onExit: deleteTestInvoice,
  steps: [
    {
      // Attend que la facture de démo (créée en arrière-plan) soit dans la liste,
      // puis surligne toute sa ligne.
      target: demoRowSelector,
      title: 'Voici votre facture',
      body: 'Une facture d’exemple a été ajoutée à votre liste. Regardons ses informations clés.',
      placement: 'left',
      lockTarget: true,
      waitTimeout: 12000,
    },
    {
      // Clic sur l'œil → ouvre le détail de la facture.
      target: '[data-tour-step="inv-eye-demo"]',
      title: 'Ouvrez la facture',
      body: 'Cliquez sur l’icône œil pour afficher le détail de la facture.',
      placement: 'left',
      clickToAdvance: true,
      waitFor: '[data-tour-step="inv-status"]',
      waitTimeout: 5000,
    },
    {
      target: '[data-tour-step="inv-status"]',
      title: 'Le statut',
      body: 'Le statut suit le cycle de vie de la facture. Une facture « En cours » arrivée à échéance et non payée est automatiquement décalée au lendemain.',
      placement: 'bottom',
    },
    {
      target: '[data-tour-step="inv-projection"]',
      title: 'La date prévisionnelle',
      body: 'C’est cette date qui détermine quand le flux est projeté dans votre dashboard.',
      placement: 'bottom',
    },
    {
      target: '[data-tour-step="inv-payment"]',
      title: 'La date de paiement',
      body: 'Une fois la facture payée, renseignez la date de paiement : le flux est alors figé à cette date. Voyons maintenant comment cette facture se projette dans votre dashboard.',
      placement: 'bottom',
      // En avançant, on quitte la page Échéances (ce qui ferme le détail) pour le
      // dashboard, on le cadre sur le mois de l'échéance et on prépare le repère.
      onNext: async () => {
        await router.push('/dashboard').catch(() => {});
        const ref = readInvoice();
        const focus = ref && ref.projection_date ? ref.projection_date : null;
        for (let i = 0; i < 25 && !document.querySelector('.cash-table'); i++) {
          await new Promise((r) => setTimeout(r, 80));
        }
        AppBus.$emit('tour:dashboard-focus', focus);
        document.getElementById('tour-row-span')?.remove();
        for (let i = 0; i < 30; i++) {
          if (buildUncatSpan()) break;
          scrollTableToBottom();
          await new Promise((r) => setTimeout(r, 150));
        }
      },
    },
    {
      // On cible la ligne « Non catégorisées » des Entrées (là où atterrit la
      // facture de démo non catégorisée) : on repère, parmi les lignes
      // libellées « Non catégorisées », celle dont les valeurs sont non nulles,
      // on la fait défiler au centre, puis on la surligne.
      target: focusUncategorizedRow,
      title: 'Dans votre dashboard',
      body: 'Votre facture client (50 000 €) atterrit dans « Non catégorisées » des Entrées, au mois de sa date prévisionnelle. C’est ainsi qu’une échéance alimente votre prévisionnel de trésorerie.',
      placement: 'top',
      lockTarget: true,
      waitTimeout: 8000,
    },
  ],
  celebration: {
    // Écran de fin "prise en main" (rail + checklist de progression) : c'est la
    // dernière étape du parcours, la checklist s'affiche complète.
    title: 'Échéances maîtrisées',
    body: 'La facture de démonstration a été supprimée.',
    actionPanel: {
      title: 'Pour aller plus loin',
      body: 'Automatisez vos imports de factures.',
      actions: [
        {
          label: 'Connecter mes outils',
          body: 'Facturation, compta… on récupère vos factures pour vous.',
          icon: 'extension',
          // La facture de démo est déjà supprimée (onExit lancé avant l'écran de
          // fin) ; on file vers les intégrations.
          onSelect: () => {
            router.push('/partner/selection').catch(() => {});
          },
        },
      ],
    },
  },
});

export {};
